/**
 * Tenant configuration — controls which menu items and routes are visible
 * based on the current domain/subdomain.
 *
 * Keys:
 *   'default' → main domain (e.g. traderbro.com, localhost)
 *   'app'     → app.traderbro.com
 *   'pro'     → pro.traderbro.com
 *   (add more keys to support additional subdomains)
 *
 * Values: array of menu item IDs from Sidebar.jsx menuItems
 * Available IDs: chat, chart, screener, history, watchlist, portfolio, alerts, research, analysts
 */
export const TENANT_MENU = {
  default: [
    'predictions',
    'leaderboard',
    'symbols',
    'following',
    'watchlist',
    'sector-map',
    'chart',
  ],
  app: [
    'chat',
    'chart',
    'screener',
    'history',
    'watchlist',
    'portfolio',
    'alerts',
    'research',
    'analysts',
    'following',
  ],
};

/**
 * Maps menu item IDs to their route paths.
 * Keep in sync with Sidebar.jsx menuItems and App.jsx routes.
 */
export const MENU_PATHS = {
  chat:        '/chat',
  chart:       '/chart',
  screener:    '/screener',
  history:     '/history',
  watchlist:   '/watchlist',
  portfolio:   '/portfolio',
  alerts:      '/alerts',
  research:    '/research-area',
  analysts:    '/analysts',
  predictions: '/predictions',
  leaderboard: '/leaderboard',
  symbols:     '/symbols',
  following:   '/following',
  'sector-map': '/sector-map',
};

/**
 * Returns the allowed menu IDs for a given subdomain.
 * Falls back to 'default' if the subdomain has no config entry.
 */
export function getAllowedMenuIds(subdomain) {
  return TENANT_MENU[subdomain] ?? TENANT_MENU.default;
}

/**
 * Returns the home path for a given subdomain — always the first allowed menu item's route.
 */
export function getHomePath(subdomain) {
  const ids = getAllowedMenuIds(subdomain);
  return MENU_PATHS[ids[0]] ?? '/chat';
}

/**
 * Maps subdomains to their logged-out landing page key.
 * Keys correspond to entries in the LANDING_PAGES map in App.jsx.
 */
export const TENANT_LANDING = {
  default: 'home',
  analysts: 'analysts',
  app: 'app',
};

/**
 * Returns the landing page key for a given subdomain.
 * null/undefined subdomain (main domain / localhost) falls back to 'default'.
 */
export function getLandingKey(subdomain) {
  if (!subdomain) return TENANT_LANDING.default ?? 'home';
  return TENANT_LANDING[subdomain] ?? TENANT_LANDING.default ?? 'home';
}
